import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FolderOpen,
  FileText,
  Users,
  Settings,
  PanelLeft,
  LogOut,
  ToggleLeft,
  ToggleRight,
  Share2,
  Calendar,
  Workflow,
  ShieldCheck,
  Globe,
  Banknote,
  Receipt,
  Images,
  Sparkles,
  BookOpen,
  MailPlus,
  GraduationCap,
} from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { useIsAdmin } from '@/hooks/useAdmin';
import { NovaIcon } from '@/components/nova';
// Wave 13a — Light/dark mode restored on web; logo swaps per
// mode so the retro wordmark reads on both palettes.
import orbitLogoWhite from '@/assets/orbit-retro-logo-white.png';
import orbitLogoBlack from '@/assets/orbit-retro-logo-black.png';
import { useThemeMode } from '@/hooks/useThemeMode';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/useAuth';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useCinemaMode } from '@/contexts/CinemaModeContext';
import { useGamification } from '@/hooks/useGamification';
import { useNovaIntelligence } from '@/hooks/useNovaIntelligence';
import { G, ORBIT_BLUE, onColor } from '@/lib/googie';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/services/organizations';
import { useSubscription } from '@/hooks/useSubscription';
import { useIsTouch } from '@/hooks/useIsIpad';

/**
 * V1 — one colour per destination, the Today quick-action palette
 * plus a few more stops so fourteen rows stay fourteen colours.
 * `onColor()` picks ink or ivory for the type on each.
 */
const RAIL_HUE = {
  today: ORBIT_BLUE,
  projects: G.violet,
  calendar: G.sky,
  galleries: G.orange,
  clients: G.teal,
  inquiries: '#F0A65A',
  expenses: G.lime,
  // INVOICE-1 — money in, beside Expenses' money out. Violet and not
  // lime, so the two are never confused at a glance in the rail.
  invoices: G.violet,
  sites: '#9AD1B0',
  emails: '#E88CB8',
  learn: G.green,
  nova: G.cyan,
  passport: G.mustard,
  admin: '#C9C2B8',
} as const;

interface RailItem {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  href: string;
  hue: string;
  /** Nova's mark is a character, not a glyph — no disc behind it. */
  nova?: boolean;
}

export const AppSidebar = () => {
  const location = useLocation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  const isTouch = useIsTouch();
  const { signOut, user } = useAuth();
  const { isDemo, isDeveloperMode, toggleDemo, canUseDemo } = useDemoMode();
  const { cinemaMode } = useCinemaMode();
  const [gamificationOn] = useGamification();
  // NOVA-OFF-2 — iOS filters .chat out of RootTabView when Nova
  // Intelligence is off. The web rail has to agree.
  const [novaOn] = useNovaIntelligence();
  const { data: organization } = useOrganization();
  const { hasDemoModeAccess } = useSubscription();
  const isAdmin = useIsAdmin();
  const isCollapsed = state === 'collapsed';
  // Wave 13a — Logo swaps with the theme mode.
  // GOOGIE-9 — and that's all it needs to do again. GOOGIE-7 had to
  // special-case the redesign because the redesign forced dark on a
  // studio who'd chosen light; now it honours the preference, so the
  // preference is once more the honest answer to "is this rail dark".
  const { mode: themeMode } = useThemeMode();
  const logoSrc = themeMode === 'dark' ? orbitLogoWhite : orbitLogoBlack;

  // Sentence case throughout per Mission Control discipline. The
  // Inquiries label honors the org's renamed form name when set.
  // Admin entry appears below Nova for users who have an active
  // admin_users row — empty list otherwise so studios never see it.
  //
  // V1 (2026-09-25) — every destination wears its own colour, the
  // way Today's quick-action chips do (see RAIL_HUE below and the
  // `.g-rail-ic` skin in googie.css). The six-destination
  // "simplified" rail is gone; this is the one list.
  const navItems: RailItem[] = [
    { icon: LayoutDashboard, label: 'Dashboard', href: '/',          hue: RAIL_HUE.today },
    { icon: FolderOpen,      label: 'Projects',   href: '/projects',  hue: RAIL_HUE.projects },
    { icon: Calendar,        label: 'Calendar',   href: '/calendar',  hue: RAIL_HUE.calendar },
    { icon: Images,          label: 'Galleries',  href: '/galleries', hue: RAIL_HUE.galleries },
    // Deliveries removed from sidebar 2026-06-18 (#316). Galleries v3
    // covers the same client-handoff use case better. The route +
    // page files are left in tree (src/pages/Deliveries.tsx,
    // DeliveryDetail.tsx) so any existing inbound deep links don't
    // 404, but the surface is no longer user-discoverable.
    { icon: Users,           label: 'Clients',    href: '/clients',   hue: RAIL_HUE.clients },
    {
      icon: FileText,
      label: organization?.intake_form_name?.trim() || 'Inquiries',
      href: '/inquiries',
      hue: RAIL_HUE.inquiries,
    },
    // INVOICE-1 — the money page. Directly above Expenses: what is
    // owed to the studio and what the studio owes read as a pair.
    { icon: Banknote,        label: 'Invoices',   href: '/invoices',  hue: RAIL_HUE.invoices },
    { icon: Receipt,         label: 'Expenses',   href: '/expenses',  hue: RAIL_HUE.expenses },
    // Workflows link killed (#341) — automations are out of the Aug 29
    // cut. Sites now sits with the other "studio output" surfaces.
    { icon: Globe,           label: 'Sites',      href: '/sites',     hue: RAIL_HUE.sites },
    // Wave EC-2 — studio-facing Email Center.
    { icon: MailPlus,        label: 'Emails',     href: '/emails',    hue: RAIL_HUE.emails },
    // NCC-2 — Learn tab.
    { icon: GraduationCap,   label: 'Learn',      href: '/learn',     hue: RAIL_HUE.learn },
    // Nova item uses the actual mascot as its icon — see ORBIT_DESIGN.md §7.
    // Hidden when Nova Intelligence is off — Settings → Nova promises
    // her tab goes away, and /assistant redirects to that switch.
    ...(novaOn
      ? [{ icon: NovaIcon, label: 'Nova', href: '/assistant', hue: RAIL_HUE.nova, nova: true } as RailItem]
      : []),
    // Passport (#435, formerly Galaxy). URL kept as /galaxy for muscle
    // memory and inbound deep-links. Hidden when gamification is off.
    ...(gamificationOn
      ? [{ icon: BookOpen, label: 'Passport', href: '/galaxy', hue: RAIL_HUE.passport } as RailItem]
      : []),
    ...(isAdmin
      ? [{ icon: ShieldCheck, label: 'Admin', href: '/admin', hue: RAIL_HUE.admin } as RailItem]
      : []),
  ];

  // Fetch user profile for avatar
  const { data: profile } = useQuery({
    queryKey: ['sidebar-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, avatar_url')
        .eq('id', user.id)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!user,
  });

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href.split('/').slice(0, 2).join('/'));
  };

  // ── CHROME-1 — the rail rides the house material ───────────────
  // The Googie skin (googie.css, GOOGIE-16 + V1) paints the rail; the
  // classes here only pick the material variant. The wrapper's own
  // `bg-sidebar` is knocked out in BOTH modes so the translucent rail
  // never sits on a cream slab (light mode used to read as grey).
  const railGlass =
    'orbit-glass orbit-glass-rail orbit-glass-frosted ' +
    '[&_[data-sidebar=sidebar]]:bg-transparent';
  const cinemaSidebarClass = cinemaMode
    ? cn(railGlass, '[--orbit-glass-tint:14_15_18_/_0.74] text-white/85')
    : themeMode === 'dark'
      ? cn(railGlass, 'text-white/85')
      : cn(railGlass, 'border-r border-sidebar-border');

  return (
    <Sidebar collapsible="icon" className={cinemaSidebarClass}>
      {/* Header with Logo.  CHROME-1 — in dark mode the divider drops
          to the glass hairline; the opaque `--sidebar-border` token
          read as a hard grey line across a translucent surface. */}
      <SidebarHeader className="border-b border-sidebar-border dark:border-white/[0.08] p-2">
        <Link to="/" className="flex items-center justify-center">
          <img
            src={logoSrc}
            alt="Orbit"
            className={cn(
              "h-10 w-auto transition-all",
              isCollapsed && "h-7"
            )}
          />
        </Link>
      </SidebarHeader>

      {/* Navigation */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const active = isActive(item.href);
                const railStyle = { '--c': item.hue, '--on': onColor(item.hue) } as React.CSSProperties;
                const button = (
                  <SidebarMenuButton
                    isActive={active}
                    onClick={() => {
                      if (isMobile) setOpenMobile(false);
                      navigate(item.href);
                    }}
                    style={railStyle}
                    className={cn(
                      'relative transition-all duration-300 rounded-full cursor-pointer',
                      active && 'font-semibold',
                    )}
                  >
                    <span className={cn('g-rail-ic', item.nova && 'g-rail-ic-nova')} aria-hidden>
                      <item.icon className="w-3.5 h-3.5 shrink-0" />
                    </span>
                    <span className={cn('g-rail-l text-[13px]', active ? 'font-semibold' : 'font-medium')}>
                      {item.label}
                    </span>
                  </SidebarMenuButton>
                );
                return (
                  <SidebarMenuItem key={item.href}>
                    {isTouch ? button : (
                      <Tooltip>
                        <TooltipTrigger asChild>{button}</TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent side="right">{item.label}</TooltipContent>
                        )}
                      </Tooltip>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer with Settings, Demo Mode, and Logout (theme toggle removed — Mission Control only) */}
      <SidebarFooter className="border-t border-sidebar-border dark:border-white/[0.08] p-2 space-y-2">
        {/* Wave 8 — Preview Studio switch. Only shown to users an
            admin has granted access to (canUseDemo) and who have
            unlocked the developer console. Studio-facing copy
            never uses the word "demo" — the label reads "Preview
            Studio" whether it's on or off. */}
        {canUseDemo && isDeveloperMode && (
          <>
            <SidebarMenu>
              {(() => {
                const demoButton = (
                  <SidebarMenuButton
                    onClick={toggleDemo}
                    className={cn(
                      'cursor-pointer',
                      isDemo ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {isDemo ? (
                      <OrbitIcon name="switch.2" fallback={ToggleRight} className="w-5 h-5" />
                    ) : (
                      <OrbitIcon name="switch.2" fallback={ToggleLeft} className="w-5 h-5" />
                    )}
                    <span className="font-medium">
                      {isDemo ? 'Preview Business · on' : 'Preview Business'}
                    </span>
                  </SidebarMenuButton>
                );
                return (
                  <SidebarMenuItem>
                    {isTouch ? demoButton : (
                      <Tooltip>
                        <TooltipTrigger asChild>{demoButton}</TooltipTrigger>
                        {isCollapsed && (
                          <TooltipContent side="right">
                            {isDemo ? 'Preview Business active — click to exit' : 'Enter Preview Business'}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    )}
                  </SidebarMenuItem>
                );
              })()}
            </SidebarMenu>

            <Separator className="bg-sidebar-border" />
          </>
        )}

        <SidebarMenu>
          {(() => {
            const settingsButton = (
              <SidebarMenuButton
                onClick={() => {
                  if (isMobile) setOpenMobile(false);
                  navigate('/settings');
                }}
                className="text-sidebar-foreground hover:text-foreground cursor-pointer"
              >
                <OrbitIcon name="gearshape" fallback={Settings} className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </SidebarMenuButton>
            );
            const signOutButton = (
              <SidebarMenuButton
                onClick={signOut}
                className="text-sidebar-foreground hover:text-destructive cursor-pointer"
              >
                <OrbitIcon name="rectangle.portrait.and.arrow.right" fallback={LogOut} className="w-5 h-5" />
                <span className="font-medium">Sign out</span>
              </SidebarMenuButton>
            );
            return (
              <>
                <SidebarMenuItem>
                  {isTouch ? settingsButton : (
                    <Tooltip>
                      <TooltipTrigger asChild>{settingsButton}</TooltipTrigger>
                      {isCollapsed && (
                        <TooltipContent side="right">Settings</TooltipContent>
                      )}
                    </Tooltip>
                  )}
                </SidebarMenuItem>
                <SidebarMenuItem>
                  {isTouch ? signOutButton : (
                    <Tooltip>
                      <TooltipTrigger asChild>{signOutButton}</TooltipTrigger>
                      {isCollapsed && (
                        <TooltipContent side="right">Sign Out</TooltipContent>
                      )}
                    </Tooltip>
                  )}
                </SidebarMenuItem>
              </>
            );
          })()}
        </SidebarMenu>

        {/* User info with profile picture */}
        {user && (
          <div className={cn(
            "pt-2 flex flex-col items-center gap-2",
            isCollapsed ? "px-0" : "px-2"
          )}>
            <Avatar className={cn(
              "transition-all",
              isCollapsed ? "w-8 h-8" : "w-8 h-8"
            )}>
              {profile?.avatar_url ? (
                <AvatarImage src={profile.avatar_url} />
              ) : null}
              <AvatarFallback className="text-sm bg-primary/10 text-primary">
                {profile?.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && (
              <p className="text-xs text-muted-foreground truncate max-w-full">{user.email}</p>
            )}
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
